// =============================================================================
// MeuDriver — LoyaltyPointsCard
// Saldo de pontos de fidelidade + histórico + botão de resgate
// =============================================================================

import { useState } from 'react'
import { Star, History, ChevronDown, ChevronUp, ArrowDownCircle, ArrowUpCircle } from 'lucide-react'
import { motion } from 'framer-motion'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Skeleton } from '@/components/ui/skeleton'
import { useLoyaltyPoints, useLoyaltyHistory } from '@/hooks/useLoyalty'
import { cn } from '@/lib/utils'
import type { LoyaltyTransactionRow } from '@/types/database'

const TYPE_LABELS: Record<string, string> = {
  lesson_completed: 'Aula concluída',
  quiz_score:       'Simulado',
  achievement:      'Conquista',
  redeemed:         'Resgate',
  expired:          'Expirado',
  bonus:            'Bônus',
}

function TransactionRow({ tx }: { tx: LoyaltyTransactionRow }) {
  const isCredit = tx.points > 0
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className={cn(
        'h-8 w-8 rounded-full flex items-center justify-center shrink-0',
        isCredit ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500',
      )}>
        {isCredit
          ? <ArrowUpCircle className="h-4 w-4" />
          : <ArrowDownCircle className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">
          {tx.description ?? TYPE_LABELS[tx.type] ?? tx.type}
        </p>
        <p className="text-xs text-muted-foreground">
          {format(parseISO(tx.created_at), "d 'de' MMM", { locale: ptBR })}
        </p>
      </div>
      <span className={cn(
        'text-sm font-bold shrink-0',
        isCredit ? 'text-green-600' : 'text-red-500',
      )}>
        {isCredit ? '+' : ''}{tx.points.toLocaleString('pt-BR')}
      </span>
    </div>
  )
}

export function LoyaltyPointsCard() {
  const { data: points, isLoading: pointsLoading } = useLoyaltyPoints()
  const { data: history, isLoading: histLoading } = useLoyaltyHistory()
  const [showHistory, setShowHistory] = useState(false)

  const visibleHistory = history?.slice(0, showHistory ? 10 : 3)

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl border border-border/60 bg-white shadow-soft overflow-hidden"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-border/50 flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
          Pontos de fidelidade
        </h2>
        {pointsLoading ? (
          <Skeleton className="h-7 w-24 rounded-full" />
        ) : (
          <span className="rounded-full bg-amber-100 px-3 py-0.5 text-sm font-bold text-amber-700 border border-amber-200">
            {(points ?? 0).toLocaleString('pt-BR')} pts
          </span>
        )}
      </div>

      <div className="px-5 py-4 space-y-3">
        {/* Info */}
        <p className="text-sm text-muted-foreground">
          Ganhe <strong>10 pontos</strong> por aula concluída. <span className="text-brand-600 font-semibold">100 pontos = R$ 1,00</span> de desconto.
        </p>

        {/* Saldo em R$ */}
        {!pointsLoading && (
          <div className="flex items-center gap-2 bg-brand-50 rounded-xl p-3 border border-brand-100">
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Valor equivalente</p>
              <p className="text-lg font-extrabold text-brand-700">
                R$ {((points ?? 0) / 100).toFixed(2).replace('.', ',')}
              </p>
            </div>
            <p className="text-xs text-muted-foreground text-right">
              Use no checkout<br/>da sua próxima aula
            </p>
          </div>
        )}

        {/* Histórico */}
        {histLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : history && history.length > 0 ? (
          <div>
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-gray-700 transition-colors mb-2"
            >
              <History className="h-3.5 w-3.5" />
              Histórico
              {showHistory
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />}
            </button>
            <div>
              {visibleHistory?.map((tx) => (
                <TransactionRow key={tx.id} tx={tx} />
              ))}
              {(history?.length ?? 0) > 3 && (
                <button
                  onClick={() => setShowHistory((v) => !v)}
                  className="mt-1 text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  {showHistory ? 'Ver menos' : `Ver mais ${history!.length - 3} transações`}
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Nenhuma transação ainda. Complete sua primeira aula para acumular pontos!
          </p>
        )}
      </div>
    </motion.section>
  )
}
